import { PART_OTHER_ID } from '@aera/schema';
import { tidy } from '@aera/ui';
import { useTranslation } from 'react-i18next';
import { Chip, OtherField, fresh, type AnswerProps } from './common.js';

/**
 * Several short parts on one screen (C3: people band, hours band, department). A part's 其他 is a field
 * typed into directly: text in it picks it (the words ride in `other`), an empty field unpicks it.
 */
export function CompoundQuestion({ rq, value, onChange, lang }: AnswerProps) {
  const { t } = useTranslation();
  const parts = value.parts ?? {};
  const pick = (partId: string, optId: string) => {
    const next = { ...parts, [partId]: optId };
    onChange({ ...fresh(value), parts: next, other: Object.values(next).includes(PART_OTHER_ID) ? value.other : undefined });
  };
  const typeOther = (partId: string, text: string) => {
    const next = { ...parts };
    if (text.trim()) next[partId] = PART_OTHER_ID;
    else delete next[partId];
    onChange({ ...fresh(value), parts: next, other: text.trim() ? text : undefined }, { debounce: true });
  };
  return (
    <div className="space-y-5">
      {rq.parts?.map((part) => (
        <div key={part.id}>
          <p id={`part-${rq.id}-${part.id}`} className="mb-2 text-[14px] font-medium text-ink-2">
            {part.label}
          </p>
          <div role="radiogroup" aria-labelledby={`part-${rq.id}-${part.id}`} className="flex flex-wrap gap-2">
            {part.options.map((o) =>
              o.id === PART_OTHER_ID ? (
                <OtherField
                  key={o.id}
                  compact
                  lang={lang}
                  checked={parts[part.id] === PART_OTHER_ID}
                  value={parts[part.id] === PART_OTHER_ID ? (value.other ?? '') : ''}
                  label={t('question.otherField', { lng: lang })}
                  onChange={(text) => typeOther(part.id, text)}
                />
              ) : (
                <Chip key={o.id} role="radio" checked={parts[part.id] === o.id} onClick={() => pick(part.id, o.id)}>
                  {tidy(o.label)}
                </Chip>
              ),
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
